import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

// Anahtar gruplaması — UI'da kategorize göstermek için
const KEY_GROUPS = {
  'Çek': ['check.portfolio', 'check.at_bank', 'check.given', 'check.dishonored'],
  'Senet': ['note.portfolio', 'note.at_bank', 'note.given', 'note.dishonored'],
  'Cari': ['cari.customer', 'cari.supplier'],
  'Kasa & Banka': ['cash.main', 'bank.main', 'other_assets.cash', 'pos.pending', 'pos.fees', 'voucher.shopping'],
  'KDV': ['vat.input', 'vat.output'],
  'Gelir & Maliyet': ['revenue.domestic', 'revenue.foreign', 'sales.return', 'sales.discount', 'cogs', 'inventory.goods'],
  'Gider': ['expense.marketing', 'expense.general', 'expense.other', 'income.other'],
  'Kambiyo': ['fx.gain', 'fx.loss'],
  'Diğer': ['payable.taxes', 'payable.personnel', 'prepaid.taxes', 'card.payable'],
};

function AccountingMappingsPage() {
  const [items, setItems] = useState([]);
  const [coa, setCoa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({}); // key → input value
  const [saving, setSaving] = useState({});   // key → bool
  const [msg, setMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        api.get('/accounting/mappings'),
        api.get('/accounting/chart-of-accounts'),
      ]);
      if (m.data.success) setItems(m.data.data.items);
      if (c.data.success) setCoa(c.data.data.items);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const itemsByKey = useMemo(() => {
    const m = {};
    for (const x of items) m[x.mapping_key] = x;
    return m;
  }, [items]);

  const validCodes = useMemo(() => new Set(coa.filter((x) => x.is_active).map((x) => x.code)), [coa]);

  const handleSave = async (key, val) => {
    setSaving((s) => ({ ...s, [key]: true }));
    setMsg(null);
    try {
      const res = await api.put(`/accounting/mappings/${key}`, { account_code: val.trim() });
      if (!res.data.success) throw new Error(res.data.message);
      await fetchAll();
      setEditing((e) => { const c = { ...e }; delete c[key]; return c; });
      setMsg({ type: 'success', text: `${key} → ${val.trim() || '(varsayılana sıfırlandı)'}` });
    } catch (err) {
      setMsg({ type: 'error', text: `${key}: ${err.response?.data?.message || err.message}` });
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  const handleReset = (key) => handleSave(key, '');

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Hesap Eşleme Tablosu</h2>
        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
          Modül operasyonları → TDHP hesabı. Şirkete özel override yapabilirsiniz; boş bırakırsanız global default'a döner.
        </span>
      </div>

      {msg && (
        <div style={{ padding: '0.5rem 0.8rem', marginBottom: '1rem', borderRadius: 4,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color:      msg.type === 'error' ? '#900' : '#060',
          border:     `1px solid ${msg.type === 'error' ? '#fcc' : '#cfc'}` }}>
          {msg.text}
        </div>
      )}

      {loading ? <div>Yükleniyor...</div> : (
        <div>
          {Object.entries(KEY_GROUPS).map(([groupName, keys]) => {
            const groupItems = keys.map((k) => itemsByKey[k]).filter(Boolean);
            if (!groupItems.length) return null;
            return (
              <section key={groupName} style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '0.95rem', margin: '0 0 0.5rem 0', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
                  {groupName}
                </h3>
                <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 4 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead style={{ background: 'var(--bg-secondary)' }}>
                      <tr>
                        <th style={th}>Anahtar</th>
                        <th style={th}>Açıklama</th>
                        <th style={th}>Default</th>
                        <th style={th}>Override (Şirket)</th>
                        <th style={th}>Etkili</th>
                        <th style={th}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupItems.map((row) => {
                        const k = row.mapping_key;
                        const isEditing = k in editing;
                        const val = isEditing ? editing[k] : (row.override_code || '');
                        const isOverride = !!row.override_code;
                        return (
                          <tr key={k} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={td}><code style={{ fontSize: '0.8rem' }}>{k}</code></td>
                            <td style={td}>{row.default_description}</td>
                            <td style={td}><code>{row.default_code}</code></td>
                            <td style={td}>
                              <input
                                value={val}
                                onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                                onFocus={() => { if (!(k in editing)) setEditing({ ...editing, [k]: row.override_code || '' }); }}
                                placeholder="(varsayılanı kullan)"
                                style={{ ...input, width: 100, fontFamily: 'monospace' }}
                                list={`coa-${k}`}
                              />
                              <datalist id={`coa-${k}`}>
                                {[...validCodes].sort().map((c) => <option key={c} value={c} />)}
                              </datalist>
                            </td>
                            <td style={td}>
                              <code style={{ fontWeight: isOverride ? 'bold' : 'normal' }}>{row.effective_code}</code>
                              {row.effective_title && <span style={{ marginLeft: 6, color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>{row.effective_title}</span>}
                            </td>
                            <td style={td}>
                              <button
                                onClick={() => handleSave(k, val)}
                                disabled={saving[k]}
                                style={{ ...btnSm, marginRight: 4 }}
                              >
                                {saving[k] ? '...' : 'Kaydet'}
                              </button>
                              {isOverride && (
                                <button
                                  onClick={() => handleReset(k)}
                                  disabled={saving[k]}
                                  style={{ ...btnSm, background: '#fafafa', color: '#666' }}
                                  title="Şirket override'ını sil, global default'a dön"
                                >
                                  Sıfırla
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

const th = { padding: '0.5rem 0.8rem', textAlign: 'left', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' };
const td = { padding: '0.5rem 0.8rem', fontSize: '0.9rem', verticalAlign: 'middle' };
const btnSm = { padding: '0.25rem 0.6rem', fontSize: '0.8rem', border: '1px solid var(--border)', background: 'var(--bg-secondary)', borderRadius: 3, cursor: 'pointer' };
const input = { padding: '0.3rem 0.5rem', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.9rem', background: 'var(--bg-secondary)', color: 'var(--text-primary)' };

export default AccountingMappingsPage;
